(function () {
  'use strict';

  function parseTargetKeys(rawValue) {
    if (Array.isArray(rawValue)) {
      return Array.from(new Set(rawValue.map((value) => String(value || '').trim()).filter(Boolean)));
    }

    const raw = String(rawValue || '').trim();
    if (!raw) return [];

    return Array.from(
      new Set(
        raw
          .split(',')
          .map((value) => String(value || '').trim())
          .filter(Boolean)
      )
    );
  }

  function patchCartItemsClass() {
    const cartItemsClass = customElements.get('cart-items');
    if (!cartItemsClass) return;

    const proto = cartItemsClass.prototype;
    if (proto.__newLogoOverlayPatched) return;

    const originalLoadContent = proto.loadLogoCustomiserModalContent;
    if (typeof originalLoadContent === 'function') {
      proto.loadLogoCustomiserModalContent = async function patchedLoadContent(modalEl, sourceUrl) {
        await originalLoadContent.call(this, modalEl, sourceUrl);

        try {
          const parsed = new URL(sourceUrl, window.location.origin);
          const targetKeys = parsed.searchParams.get('target_keys');
          if (!targetKeys) return;

          const customiserEl = modalEl?.querySelector?.('logo-customiser');
          if (!customiserEl) return;

          customiserEl.setAttribute('data-target-keys', targetKeys);
          customiserEl.targetKeys = parseTargetKeys(targetKeys);
        } catch (error) {
          // ignore
        }
      };
    }

    proto.__newLogoOverlayPatched = true;
  }

  function patchLogoCustomiserClass() {
    const logoCustomiserClass = customElements.get('logo-customiser');
    if (!logoCustomiserClass) return;

    const proto = logoCustomiserClass.prototype;
    if (proto.__newLogoOverlayPatched) return;

    const originalConnectedCallback = proto.connectedCallback;
    if (typeof originalConnectedCallback === 'function') {
      proto.connectedCallback = function patchedConnectedCallback() {
        const result = originalConnectedCallback.apply(this, arguments);

        try {
          const params = new URLSearchParams(window.location.search);
          const parsedTargetKeys = parseTargetKeys(
            params.get('target_keys') || this.getAttribute('data-target-keys') || this.targetKeys || ''
          );
          const targetKey =
            params.get('key') ||
            this.getAttribute('data-target-key') ||
            String(this.targetKey || '').trim() ||
            '';

          if (targetKey && !parsedTargetKeys.includes(targetKey)) {
            parsedTargetKeys.unshift(targetKey);
          }

          if (parsedTargetKeys.length) {
            this.targetKeys = parsedTargetKeys;
          }
        } catch (error) {
          // ignore
        }

        return result;
      };
    }

    const originalHandleSubmit = proto.handleSubmit;
    if (typeof originalHandleSubmit === 'function') {
      proto.handleSubmit = async function patchedHandleSubmit(event) {
        const config = window.NewLogoConfig || {};
        const multiTargetEnabled = config.featureFlags?.multiTargetCustomiserSave !== false;

        const params = new URLSearchParams(window.location.search);
        const computedTargetKeys = parseTargetKeys(
          params.get('target_keys') || this.getAttribute('data-target-keys') || this.targetKeys || ''
        );

        if (!multiTargetEnabled || computedTargetKeys.length <= 1) {
          return originalHandleSubmit.call(this, event);
        }

        this.targetKeys = computedTargetKeys;

        event.preventDefault();
        if (this.isSubmitting) return;

        this.clearError?.();

        if (
          typeof this.collectPayload !== 'function' ||
          typeof this.fetchCart !== 'function' ||
          typeof this.buildProperties !== 'function' ||
          typeof this.changeLineItem !== 'function'
        ) {
          return originalHandleSubmit.call(this, event);
        }

        const payload = this.collectPayload();
        if (!payload) return;

        this.isSubmitting = true;
        this.setLoading?.(true);

        try {
          const cartBefore = await this.fetchCart();
          const targetItems = (cartBefore.items || []).filter((item) => computedTargetKeys.includes(item.key));

          if (!targetItems.length) {
            throw new Error('This cart line no longer exists. Please return to cart and try again.');
          }

          if (typeof this.removeLegacyChargeLines === 'function') {
            await this.removeLegacyChargeLines(cartBefore);
          }

          let changedCart = null;
          const submitTokenSeed = `${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
          const sharedToken =
            targetItems.length > 1
              ? String(targetItems[0]?.properties?._logo_customiser_token || '').trim() || submitTokenSeed
              : '';
          const sharedSetupPricing =
            typeof this.buildPricingDetails === 'function'
              ? this.buildPricingDetails(payload, targetItems[0]?.quantity || 0)
              : { setupFeeCents: 0 };
          const sharedSetupFeeCents = payload.setupRequired ? Number(sharedSetupPricing.setupFeeCents) || 0 : 0;

          for (let index = 0; index < targetItems.length; index += 1) {
            const targetItem = targetItems[index];
            const payloadForTarget = index === 0 ? payload : { ...payload, setupRequired: false };
            const pricing =
              typeof this.buildPricingDetails === 'function'
                ? this.buildPricingDetails(payloadForTarget, targetItem.quantity)
                : {
                    unitCents: 0,
                    positionsCount: 0,
                    smallCount: 0,
                    largeCount: 0,
                    setupFeeCents: 0,
                    extraPerItemCents: 0,
                    extraLineCents: 0,
                  };

            const token =
              sharedToken ||
              String(targetItem?.properties?._logo_customiser_token || '').trim() ||
              `${submitTokenSeed}-${index}`;
            const properties = this.buildProperties(targetItem.properties || {}, payloadForTarget, pricing, token);
            properties._logo_setup_group_fee_cents = String(sharedSetupFeeCents || 0);
            const hasUploadArtwork =
              Array.isArray(payload.logoArtworks) &&
              payload.logoArtworks.some((artwork) => artwork?.kind === 'file' && artwork.file);

            if (hasUploadArtwork && typeof this.changeLineItemWithFile === 'function') {
              // eslint-disable-next-line no-await-in-loop
              changedCart = await this.changeLineItemWithFile(
                targetItem.key,
                targetItem.quantity,
                properties,
                Array.isArray(payload.logoArtworks) ? payload.logoArtworks : []
              );
            } else {
              // eslint-disable-next-line no-await-in-loop
              changedCart = await this.changeLineItem(targetItem.key, targetItem.quantity, properties);
            }
          }

          if (!changedCart) {
            throw new Error('Could not update the selected cart item. Please try again.');
          }
          const latestCart =
            typeof this.getFreshCartPayload === 'function' ? await this.getFreshCartPayload(changedCart) : changedCart;

          if (this.isModalMode) {
            this.publishCartUpdate?.(latestCart);
            document.dispatchEvent(
              new CustomEvent('cart:updated', {
                detail: {
                  cart: latestCart,
                  source: 'logo-customiser',
                },
              })
            );
            window.dispatchEvent(
              new CustomEvent('logo-customiser:saved', {
                detail: {
                  modalId: this.modalId || null,
                  cart: latestCart,
                },
              })
            );
            return;
          }

          if (window.self !== window.top) {
            window.parent.postMessage(
              {
                type: 'logo-customiser:saved',
                modalId: this.modalId || null,
              },
              window.location.origin
            );
            return;
          }

          window.location.href = '/cart';
        } catch (error) {
          console.error(error);
          this.showError?.(error.message || 'Unable to save your customisation right now. Please try again.');
        } finally {
          this.isSubmitting = false;
          this.setLoading?.(false);
        }
      };
    }

    proto.__newLogoOverlayPatched = true;
  }

  function init() {
    patchCartItemsClass();
    patchLogoCustomiserClass();

    if (window.customElements && typeof window.customElements.whenDefined === 'function') {
      window.customElements.whenDefined('cart-items').then(() => {
        patchCartItemsClass();
      });
      window.customElements.whenDefined('logo-customiser').then(() => {
        patchLogoCustomiserClass();
      });
    }
  }

  init();

  window.NewLogoCustomiserOverlay = {
    init,
  };
})();
